import { ElementCommonPropertyConverter } from '@src/slide/domain/ui-model/converter/node-property/element/element-common-property-converter';
import { BorderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/border/border-converter';
import { EffectConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/effect/effect-converter';
import { FillConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/fill/fill-converter';
import { GraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/graphic-info/graphic-info-converter';
import { ILinkAttr, LinkConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/link/link-converter';
import { LockingConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/locking/locking-converter';
import { PictureInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/picture-info/picture-info-converter';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { DeltaOf, IPictureData, IPictureProps } from '@tencent/slide-sdk-model';
import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { Locks } from '@src/slide/domain/ui-model/element-modeles/property/lock/lock.interface';
import { ShapeType } from '@src/slide/infrastructure/module/dependent/shape-info/graphic-type';
import { CustomGraphicInfoConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/custom-graphic-info/custom-graphic-info-converter';
import { AttachmentConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/attachment/attachment-converter';
import { AddonConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/addon/addon-converter';
import { NameConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/name/name-converter';
import { PlaceholderConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/placeholder/placeholder-converter';
import { Element3DConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/3d/element-3d-converter';

/**
 * 图片
 */
export class PicturePropertyConverter {
    /**
     * 图片 sdk -> ui 属性映射
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<IPictureProps>>,
        uiData: IPictureModel,
    ): void {
        // **** 公共属性 ****

        // 占位符信息转换
        if (sdkData.nvPr?.ph) {
            PlaceholderConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.ph, uiData);
        }
        // frame
        ElementCommonPropertyConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
        // 形状绘图属性
        if (sdkData.spPr?.prstGeom) {
            GraphicInfoConverter.convertPartialDataFromSdkToUi(sdkData.spPr.prstGeom, uiData.props.style);
        }
        // 自定义图形信息转换
        if (sdkData.spPr?.custGeom) {
            CustomGraphicInfoConverter.convertPartialDataFromSdkToUi(sdkData.spPr.custGeom, uiData.props.style);
        } else {
            uiData.props.style.pathList = [];
        }
        // 填充
        if (sdkData.spPr && FillConverter.needConvertSdkData(sdkData.spPr)) {
            // 对齐以前逻辑，每次都重新覆盖一个新对象
            uiData.props.style.fillBackground = {};
            FillConverter.convertPartialDataFromSdkToUi(sdkData.spPr, uiData.props.style.fillBackground);
            if (!Object.keys(uiData.props.style.fillBackground).length) {
                delete uiData.props.style.fillBackground;
            }
        } else if (uiData.props.style.fillBackground) {
            delete uiData.props.style.fillBackground;
        }
        // 边框
        if (sdkData.spPr?.ln) {
            uiData.props.style.border = uiData.props.style.border || {};
            BorderConverter.convertPartialDataFromSdkToUi(sdkData.spPr.ln, uiData.props.style.border);
            BorderConverter.convertControlValuesFromLineSketch(sdkData.spPr.ln, uiData.props.style);
        } else if (uiData.props.style.border) {
            delete uiData.props.style.border;
        }
        // 3d
        if (sdkData.spPr?.scene3d) {
            uiData.scene3d = uiData.scene3d ?? {};
            Element3DConverter.convertScene3DFromSdkToUi(sdkData.spPr.scene3d, uiData.scene3d);
        } else {
            uiData.scene3d = undefined;
        }
        if (sdkData.spPr?.sp3d) {
            uiData.shape3d = uiData.shape3d ?? {};
            Element3DConverter.convertShape3DFromSdkToUi(sdkData.spPr.sp3d, uiData.shape3d);
        } else {
            uiData.shape3d = undefined;
        }
        // 锁定纵横比
        if (sdkData.nvPr) {
            LockingConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.locking, uiData.props, true);
        }
        // 链接
        if (sdkData.nvPr?.hlinkClick && !sdkData.video && !sdkData.audio) {
            LinkConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.hlinkClick, uiData.props);
        } else if (uiData.props[ILinkAttr.link]) {
            delete uiData.props[ILinkAttr.link];
        }
        // 附件
        if (sdkData.nvPr?.attachment) {
            AttachmentConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.attachment, uiData.props);
        }
        // 插件
        if (sdkData.nvPr?.addon) {
            AddonConverter.convertPartialDataFromSdkToUi(sdkData.nvPr.addon, uiData.props);
        }
        // 名称
        if (sdkData.nvPr?.name) {
            NameConverter.convertPartialDataFromSdkToUi(sdkData.nvPr, uiData);
        }
        // 效果
        if (sdkData.spPr?.effectLst) {
            uiData.props.style.effect = uiData.props.style.effect || {};
            EffectConverter.convertPartialDataFromSdkToUi(sdkData.spPr.effectLst, uiData.props.style.effect);
        } else if (uiData.props.style.effect) {
            delete uiData.props.style.effect;
        }

        // **** 独有属性 ****

        // 图片独有属性信息（1、src以及backupSrc 2、imageType 3、透明度 4、图片校正/效果 5、裁剪 6、音视频 7、相对图片原始尺寸比例缩放）
        PictureInfoConverter.convertPartialDataFromSdkToUi(sdkData, uiData);
    }

    /**
     * 图片 ui -> sdk 属性映射
     * @param uiData
     * @param sdkData
     * @param rawUiData
     */
    // eslint-disable-next-line complexity
    public static convertPartialDataFromUiToSdk(
        uiData: DeepPartial<IPictureModel>,
        sdkData: DeltaOf<IPictureData>,
        rawUiData: IPictureModel,
    ): void {
        sdkData.spPr = sdkData.spPr || {};
        sdkData.nvPr = sdkData.nvPr || {};

        // **** 公共属性 ****

        // 占位符信息转换
        const placeholderInfo = uiData.props?.style?.placeholder || uiData.placeHolder;
        if (placeholderInfo) {
            sdkData.nvPr.ph = sdkData.nvPr.ph || {};
            PlaceholderConverter.convertPartialDataFromUiToSdk(placeholderInfo, sdkData.nvPr.ph);
        }
        // frame
        ElementCommonPropertyConverter.convertPartialDataFromUiToSdk(uiData, sdkData, rawUiData);
        // 形状绘图属性
        if (uiData?.props?.style?.bgShape || uiData?.props?.style?.controlValues) {
            if ((uiData?.props?.style?.bgShape || rawUiData.props.style?.bgShape) !== ShapeType.custom) {
                sdkData.spPr.prstGeom = GraphicInfoConverter.convertPartialDataFromUiToSdk({
                    bgShape: uiData.props.style.bgShape ?? rawUiData.props?.style?.bgShape,
                    controlValues: uiData.props.style.controlValues,
                });
                sdkData.spPr.custGeom = null;
            } else {
                // 草绘 历史遗留 后续考虑优化
                // @ts-ignore
                const { customGeom } = uiData.props.style;
                if (customGeom?.pathList?.length) {
                    sdkData.spPr.custGeom = sdkData.spPr.custGeom || {};
                    CustomGraphicInfoConverter.convertPartialDataFromUiToSdk(customGeom, sdkData.spPr.custGeom);
                }
                sdkData.spPr.prstGeom = null;
            }
        }
        // 填充
        if (uiData.props?.style?.fillBackground) {
            FillConverter.convertPartialDataFromUiToSdk(uiData.props.style.fillBackground, sdkData.spPr);
        } else if (uiData.props?.style?.fillBackground === null) {
            ['grpFill', 'blipFill', 'pattFill', 'solidFill', 'gradFill'].forEach((p) => {
                if (rawUiData?.sdkData?.props?.spPr?.[p] && sdkData.spPr) {
                    sdkData.spPr[p] = null;
                }
            });
        }

        // 边框
        if (uiData.props?.style?.border) {
            sdkData.spPr.ln = sdkData.spPr.ln || {};
            BorderConverter.convertPartialDataFromUiToSdk(uiData.props.style.border, sdkData.spPr.ln, rawUiData);
        }
        // 锁定纵横比
        if (uiData.props?.[Locks.LOCKS]) {
            sdkData.nvPr.locking = sdkData.nvPr.locking || {};
            LockingConverter.convertPartialDataFromUiToSdk(uiData.props, sdkData.nvPr.locking);
        }
        // 链接
        if (uiData.props?.link) {
            sdkData.nvPr.hlinkClick = sdkData.nvPr.hlinkClick || {};
            LinkConverter.convertPartialDataFromUiToSdk(uiData.props, sdkData.nvPr.hlinkClick);
        } else if (uiData.props?.link === null) {
            sdkData.nvPr.hlinkClick = null;
        }
        // 附件
        if (uiData.props?.attachment) {
            AttachmentConverter.convertPartialDataFromUiToSdk(uiData.props, sdkData.nvPr);
        }
        // 插件
        if (uiData.props?.addon) {
            AddonConverter.convertPartialDataFromUiToSdk(uiData.props, sdkData.nvPr);
        }
        // 名称
        if (uiData.name) {
            NameConverter.convertPartialDataFromUiToSdk(uiData, sdkData.nvPr);
        }
        // 效果
        if (uiData.props?.style?.effect) {
            sdkData.spPr.effectLst = sdkData.spPr.effectLst || {};
            EffectConverter.convertPartialDataFromUiToSdk(uiData.props.style.effect, sdkData.spPr.effectLst);
        }

        // 3d
        if (uiData.props?.scene3d) {
            sdkData.spPr.scene3d = sdkData.spPr.scene3d ?? {};
            Element3DConverter.convertScene3DFromUiToSdk(uiData.props.scene3d, sdkData.spPr.scene3d);
        } else if (uiData.props?.scene3d === null) {
            sdkData.spPr.scene3d = null;
        }
        if (uiData.props?.shape3d) {
            sdkData.spPr.sp3d = sdkData.spPr.sp3d ?? {};
            Element3DConverter.convertShape3DFromUiToSdk(uiData.props.shape3d, sdkData.spPr.sp3d);
        } else if (uiData.props?.shape3d === null) {
            sdkData.spPr.sp3d = null;
        }

        // **** 独有属性 ****

        // 图片独有属性信息（1、src以及backupSrc 2、imageType 3、透明度 4、图片校正/效果 5、裁剪 6、音视频 7、相对图片原始尺寸比例缩放）
        PictureInfoConverter.convertPartialDataFromUiToSdk(uiData, sdkData, rawUiData);
    }
}
